import { queryKeys } from '@query/keys'
import { useQuery } from '@tanstack/react-query'
import { FormikErrors, FormikProps, withFormik } from 'formik'
import { t } from 'i18next'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Form } from '@lib/formik'

import { useAccountReadContext } from '@context/AccountContextContext'
import { useSessionEntity } from '@context/EntityContext'
import { useModal } from '@context/ModalsContext'

import { Body1, Heading6 } from '@ui/Typography'
import { Button } from '@ui/components/Button'
import { removeUploadedFile } from '@ui/components/FileUpload/FileUpload'
import { FileData } from '@ui/components/FileUpload/types'

import { useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'
import { TickmillCompaniesEnum } from '@utils/companyName.utils'
import { isDoubleSidedDocument } from '@utils/uploadDocument'

import { ClientNciPriorityDto } from '@model/ClientNciPriorityDto'
import { CreateClientDocument } from '@model/CreateClientDocuments'
import { ClientCreateNciFinalHistory, ClientNciHistoryStatus } from '@model/CreateNciHistory'
import { DocumentCategoryType } from '@model/DocumentCategories'
import { UploadDocumentCategoryEnum } from '@model/UploadDocumentCategoryEnum'

import { useOnLocaleSwitch } from '@hooks/useOnLocaleSwitch'

import { Box } from '@shared/components/Box'
import { ConfirmModal } from '@shared/modals/Confirm.modal'

import { DocumentFileUploadsFactory } from '../DocumentFileUploadsFactory'
import { DocumentNumberFieldFactory } from './DocumentNumberFieldFactory'
import { DocumentChooseAnotherModal } from './modals/DocumentChooseAnother.modal'
import { DocumentConfirmationModal } from './modals/DocumentConfirmation.modal'
import { DocumentReasonModal } from './modals/DocumentReason.modal'

import styles from '../DocumentsInfoPage.module.scss'

export interface DocumentInfoPageFormValuesStep1 {
  documentTypeId: string
  documents: DocumentManagementDocument[]
  reason?: string
  files: FileData[]
  nciDocumentNumber: string
}

interface DocumentManagementDocument {
  categoryId: number
  typeId: number
  file: string
  filename: string
  error?: boolean
}

const DocumentInfoNCIPageStep1FormUI: React.FC<
  FormikProps<DocumentInfoPageFormValuesStep1> & OuterProps
> = (props) => {
  const {
    setValues,
    values,
    onSkipDocuments,
    isValid,
    handleSubmit,
    documentCategory,
    handleChoosePreviousDoc,
    accountNciAvailablePriorities,
    isSubmitting,
  } = props
  useOnLocaleSwitch(() => props.validateForm())

  const { t } = useTranslation()
  const entity = useSessionEntity()
  const apiClient = useApiClient(ClientApiClient)
  const { account } = useAccountReadContext()
  const [showChooseAnotherDoc, setShowChooseAnotherDoc] = useState(false)
  const skipUploadModal = useModal(DocumentConfirmationModal, { onConfirm: onSkipDocuments })
  const deleteFileConfirmModal = useModal(ConfirmModal, {
    title: t('Sign up.Remove Document'),
    description: t('Sign up.Do you really want to delete this document?'),
    primaryLabel: t('Yes'),
    secondaryLabel: t('Cancel'),
  })
  const chooseAnotherModal = useModal(DocumentChooseAnotherModal)
  const reasonModal = useModal(DocumentReasonModal)
  const [documentOptions, setDocumentOptions] = useState<ClientNciPriorityDto[]>([])

  const { data: documents } = useQuery({
    queryKey: queryKeys.signup.nciDocumentTypes(),
    queryFn: () => apiClient.getAccountNciPriorities(),
  })

  useEffect(() => {
    if (documents) {
      setDocumentOptions(documents.filter((doc) => !doc.final))
    }
  }, [documents])

  const handleSubmitForm = async (e: React.FormEvent) => {
    e.preventDefault()

    try {
      await handleSubmit()
    } catch (error: unknown) {
      console.log(error)
    }
  }

  const handleSubmitReason = async (reason?: string) => {
    if (reason) {
      props.handleSubmitReason(reason)
    }
  }

  const handleChooseAnotherDocument = () => {
    if (accountNciAvailablePriorities.final && documentOptions.length) {
      if (documentOptions.length > 1) {
        chooseAnotherModal.open({
          documentOptions,
          onConfirm: (doc: ClientNciPriorityDto, count?: number) =>
            handleDocumentTypeChange(doc, count ?? 0),
        })
      } else {
        handleChoosePreviousDoc(documentOptions[0], 0)
      }
    } else {
      reasonModal.open({
        onSubmit: handleSubmitReason,
        documentType: props.accountNciAvailablePriorities.nciRule.shortName || '',
      })
    }
  }

  const handleDocumentUploads = (filesData: FileData[]) => {
    setValues({
      ...values,
      files: filesData,
      documents: filesData.map((x) => ({
        ...x,
        categoryId: DocumentCategoryType.Personal,
        typeId: Number.parseInt(values.documentTypeId, 10),
        filename: x.fileName,
        file: x.base64Content,
      })),
    })
  }

  const handleDocumentRemove = (fileToRemove: FileData) => {
    const files = removeUploadedFile(values.files, fileToRemove)
    setValues({
      ...values,
      files,
      documents: files.map((x) => ({
        ...x,
        categoryId: DocumentCategoryType.Address,
        typeId: Number.parseInt(values.documentTypeId, 10),
        filename: x.fileName,
        file: x.base64Content,
      })),
    })
  }

  const handleDocumentBeforeRemove = (fileToRemove: FileData) => {
    deleteFileConfirmModal.open({ onConfirm: () => handleDocumentRemove(fileToRemove) })
  }

  const handleDocumentTypeChange = (doc: ClientNciPriorityDto, count: number) => {
    handleChoosePreviousDoc(doc, count)
  }

  useEffect(() => {
    setValues({
      ...values,
      files: [],
      documents: [],
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.documentTypeId])

  useEffect(() => {
    if (documentOptions.length) {
      setShowChooseAnotherDoc(true)
    } else {
      setShowChooseAnotherDoc(false)
    }
  }, [accountNciAvailablePriorities.final, documentOptions])

  const isEUEntity = useSessionEntity() === TickmillCompaniesEnum.TICKMILL_EU
  const isNationalityUK = account?.nationalities.map((x) => x.id).includes('GBR')

  return (
    <Form onSubmit={handleSubmitForm}>
      <Heading6 medium>{t('Sign up.NCI: Process')}</Heading6>
      <Body1 isParagraph>
        {t('Sign up.Please be informed that your Primary Nationality is')}{' '}
        {props.accountNciAvailablePriorities.country.name}.{' '}
        {t('Sign up.If you have any reservations, please contact', {
          entity: isEUEntity ? 'eu' : 'co.uk',
        })}
      </Body1>
      <br />
      <Body1 isParagraph className='pb-5'>
        {t('Sign up.In accordance with the new regulations of the Markets in', {
          entity: isEUEntity ? t('Europe') : t('UK'),
        })}
      </Body1>
      <div className='pb-5'>
        {entity === TickmillCompaniesEnum.TICKMILL_UK && isNationalityUK && (
          <div className='pb-3'>
            <Body1 medium>{t('Sign up.Please provide your UK National Insurance Number')}</Body1>
          </div>
        )}
        <DocumentNumberFieldFactory
          accountNciAvailablePriorities={props.accountNciAvailablePriorities}
          handleChooseAnotherDocument={
            showChooseAnotherDoc ? handleChooseAnotherDocument : undefined
          }
        />
        <div className='pb-5'>
          <div className='pb-3'>
            <Body1 medium>{t('Sign up.Proof of ID-Document')}</Body1>
          </div>
          <div className='pb-4'>
            <Body1 isParagraph>{t('Sign up.An identification document')}</Body1>
          </div>
          <div>
            <div className='pb-3'>
              <DocumentFileUploadsFactory
                files={values.files}
                documentTypeId={values.documentTypeId}
                documentCategory={documentCategory}
                onChange={handleDocumentUploads}
                onBeforeRemove={handleDocumentBeforeRemove}
              />
            </div>
          </div>
        </div>
      </div>

      <Box flex justify='center'>
        <div className={styles.button}>
          <Button.Secondary size='lg' fullWidth onClick={() => skipUploadModal.open()}>
            {t('Skip')}
          </Button.Secondary>
          <Button.Primary size='lg' type='submit' fullWidth disabled={!isValid || isSubmitting}>
            {t('Submit')}
          </Button.Primary>
        </div>
      </Box>
    </Form>
  )
}

interface OuterProps {
  accountNciAvailablePriorities: ClientNciPriorityDto

  documentCategory: UploadDocumentCategoryEnum

  onSkip(): void

  onSkipDocuments(): void

  handleSubmitReason(reason: string): void

  handleChoosePreviousDoc(doc: ClientNciPriorityDto, count: number): void

  handleSubmit(values: ClientCreateNciFinalHistory, documents: CreateClientDocument[]): void
}

function dataToSubmit(
  values: DocumentInfoPageFormValuesStep1,
  accountNciAvailablePriorities: ClientNciPriorityDto
): ClientCreateNciFinalHistory {
  return {
    documentTypeId: Number(values.documentTypeId),
    nciDocumentNumber: values.nciDocumentNumber,
    nciPriorityId: accountNciAvailablePriorities.id,
    countryId: accountNciAvailablePriorities.country.id,
    statusId: ClientNciHistoryStatus.PENDING,
  }
}

export const DocumentInfoNCIPageStep1Form = withFormik<OuterProps, DocumentInfoPageFormValuesStep1>(
  {
    mapPropsToValues: ({ accountNciAvailablePriorities }) => {
      const [documentType] = accountNciAvailablePriorities.documentTypes
      return {
        nciDocumentNumber: '',
        files: [],
        documentTypeId: documentType?.documentTypeId.toString() || '',
        documents: [],
      }
    },
    handleSubmit: async (values, { props, setSubmitting }) => {
      try {
        await props.handleSubmit(
          dataToSubmit(values, props.accountNciAvailablePriorities),
          values.documents
        )
      } finally {
        setSubmitting(false)
      }
    },
    validate: (values, props) => {
      const errors: FormikErrors<DocumentInfoPageFormValuesStep1> = {}

      if (values.nciDocumentNumber === '') {
        errors.nciDocumentNumber = t('Validation.Required')
      }

      const documents = values.documents.filter((x) => !!x?.filename).filter((x) => !x.error)
      if (
        documents.length <=
        (isDoubleSidedDocument(props.documentCategory, Number(values.documentTypeId)) ? 1 : 0)
      ) {
        errors.documents = t('Validation.Required')
      }

      if (values.documents.some((doc) => doc.error)) {
        errors.documents = t('Validation.Required')
      }

      return errors
    },
    enableReinitialize: true,
    isInitialValid: false,
  }
)(DocumentInfoNCIPageStep1FormUI)
